import { BundleOptions, bundleOptionsFromQuery } from 'src/components/BundleProduct/utils/bundleOptionsFromQuery';
import invariant from 'tiny-invariant';
import { wearerBundleDetail_wearerBundleDetail } from 'src/queries/__generated__/wearerBundleDetail';
import { CollatedData } from 'src/components/WeareBundleProduct/hooks/useWearerBundleFormState';
import { BundleDataBundleOptionInterface } from 'swagger/ts/Definitions';
import { MaybeCustomisation } from 'src/components/BundleProduct/utils/formDataToPayload';
import { MediaEntry } from 'src/types/global-types';

export type WearerOptions = {
    bundle_options: BundleOptions;
    max_qty: number;
    sku: string;
    id: number;
    description: string;
    media_gallery: MediaEntry[];
    is_24_hour_delivery: boolean;
    can_customise: boolean;
    name: string;
    size_guides: string;
};

export const name = 'Wearer';

export type WearerOptionMap = Map<number, WearerOptions>;

export function mixAndMatchQueryToWearerOptionsMap(item): WearerOptionMap {
    invariant(item.__typename === 'WearerBundlesProduct', 'only WearerBundlesProduct supported');
    const output: WearerOptionMap = new Map();

    (item.bundle_items || []).forEach((bundleItem) =>
        bundleItem?.target_configurable.forEach((item) => {
            if (item.option_id === bundleItem.option_id) {
                invariant(item?.id, 'id must be present');
                invariant(bundleItem?.options, 'item.options must be present');
                const wearerOption: WearerOptions = {
                    bundle_options: bundleOptionsFromQuery(item.id, bundleItem.options),
                    max_qty: bundleItem.max_qty ?? 1,
                    sku: item.sku || 'unknown-sku',
                    id: item.id || -1,
                    description: item.description.html || '',
                    media_gallery: item.media_gallery,
                    is_24_hour_delivery: bundleItem.is_24_hour_delivery,
                    can_customise: bundleItem.can_customise,
                    name: item.name || '',
                    size_guides: item.size_guides?.content,
                };
                const key = item.id;
                output.set(key, wearerOption);
            }
        }),
    );

    return output;
}

export function queryToWearerOptionsMap(item: wearerBundleDetail_wearerBundleDetail): WearerOptionMap {
    invariant(item.__typename === 'WearerBundlesProduct', 'only WearerBundlesProduct supported');
    const output: WearerOptionMap = new Map();

    (item.bundle_items || []).forEach((bundleItem) => {
        const targetConfigurable = bundleItem?.target_configurable?.find(
            (element) => element?.option_id === bundleItem.option_id,
        );
        invariant(targetConfigurable?.id, 'id must be presen');
        invariant(bundleItem?.options, 'item.options must be present');
        const wearerOption: WearerOptions = {
            bundle_options: bundleOptionsFromQuery(bundleItem.option_id, bundleItem.options),
            max_qty: bundleItem.max_qty ?? 1,
            sku: targetConfigurable.sku || 'unknown-sku',
            id: targetConfigurable.id || -1,
            description: targetConfigurable.description.html || '',
            media_gallery: targetConfigurable.media_gallery,
            is_24_hour_delivery: item.is_24_hour_delivery,
            can_customise: item.can_customise,
            name: targetConfigurable.name || '',
            size_guides: targetConfigurable.size_guides?.content,
        };
        const key = targetConfigurable.id;
        output.set(key, wearerOption);
    });

    return output;
}

export function createWearerBundlePayload(
    sku: string,
    items: Record<number, CollatedData>,
    customisation: MaybeCustomisation[] | undefined,
    // Magento did not create properly types
    // ): QuoteDataCartItemInterface {
): any {
    const bundleOptions: BundleDataBundleOptionInterface[] = [];
    Object.keys(items).forEach((productId) => {
        const opts = items[productId]?.atbPayload?.product_option?.extension_attributes?.bundle_options || [];
        if (opts.length === 0) {
            console.warn('bundle_options missing from %s', productId);
            return;
        }
        bundleOptions.push(...opts);
    });
    return {
        sku,
        qty: 1,
        quote_id: '-1', // this get overriden later, put here to satify the types
        product_option: {
            extension_attributes: {
                bundled_customisation: customisation,
                bundle_options: bundleOptions,
            },
        },
    };
}
